// Dataset Hooks
import { useEffect, useState } from 'react';
import { RefLocale } from './LanguageHooks';
import {
  fetchEmailData,
  fetchHouseData,
  fetchParticipantData,
  fetchParticipantPlacementData,
  fetchActionItems,
  fetchPhoneData,
  fetchPhonePlacementData,
  fetchReceivedSensorFiles,
  fetchSensorData,
  fetchSensorHousePlacementData,
  fetchWearableData,
  fetchWearablePlacementData,
  setAllDatasetsToLoading,
  fetchCollectionRecords,
  fetchCoverageSummary,
  fetchBatteryChangeRecordsCodes,
} from '../store/actions/datasetActions';
import { resetAllDatasets } from '../store/actions/generalStoreActions';
import * as libCon from '../community-hats-js-library/Constants.js';
import * as locCon from '../LocalConstants';
import * as dm_fun from '../utils/dataManipulationFunctions';
import { RefStoredValue, setStoredValue } from './GeneralStoreHooks';
import { isNullOrUndefined, sleep } from '../community-hats-js-library/utils/generalFunctions.js';
import { getApplictationVersion } from '../community-hats-js-library/back-end/router';

export const refreshAllDatasets = async () => {
  // Resets All Datasets
  resetAllDatasets();
  setAllDatasetsToLoading();

  fetchReceivedSensorFiles();

  // Gets the latest location tracker app version
  let [resp, version] = await getApplictationVersion(libCon.LOCATION_TRACKER_APP);
  if (resp === libCon.OK) setStoredValue(locCon.LOCATION_TRACKER_LATEST_APP_VERSION, version);
  else setStoredValue(locCon.LOCATION_TRACKER_LATEST_APP_VERSION, null);

  fetchHouseData();
  fetchEmailData();
  await sleep(1000);
  fetchParticipantPlacementData();
  fetchSensorData();
  await sleep(1000);
  fetchSensorHousePlacementData();
  fetchPhoneData();
  await sleep(1000);
  fetchPhonePlacementData();
  fetchWearableData();
  await sleep(1000);
  fetchWearablePlacementData();
  fetchActionItems();
  fetchBatteryChangeRecordsCodes();
  await sleep(1000);
  fetchCollectionRecords();
  fetchParticipantData();
  fetchCoverageSummary();
};

export const RefParticipantsData = () => {
  const allParticipants = RefStoredValue(libCon.DATASET_PARTICIPANTS);
  const statusParticipants = RefStoredValue(libCon.STATUS_DATASET_PARTICIPANTS);

  const [participants, setParticipants] = useState(() => null);
  const [status, setStatus] = useState(() => libCon.STATUS_NOT_STARTED);

  useEffect(() => {
    let newStatus = statusParticipants;

    if (newStatus === libCon.OK) {
      if (!isNullOrUndefined(allParticipants)) {
        setParticipants(dm_fun.filterOnlySEWA({ allParticipants }));
        setStatus(libCon.OK);
      } else setStatus(libCon.LOADING);
    } else setStatus(newStatus);
  }, [allParticipants, statusParticipants]);

  return [status, participants];
};

export const RefAllActionItemsWithParticipants = () => {
  // Participants
  const allParticipants = RefStoredValue(libCon.DATASET_PARTICIPANTS);
  const statusParticipants = RefStoredValue(libCon.STATUS_DATASET_PARTICIPANTS);

  // Action Items
  const allActionItems = RefStoredValue(libCon.DATASET_ISSUES);
  const statusActionItems = RefStoredValue(libCon.STATUS_DATASET_ISSUES);

  const [actionItems, setActionItems] = useState(() => null);
  const [status, setStatus] = useState(() => libCon.STATUS_NOT_STARTED);

  useEffect(() => {
    let arr = [statusParticipants, statusActionItems];

    let newStatus = libCon.STATUS_NOT_STARTED;

    if (arr.some((s) => s === libCon.LOADING)) newStatus = libCon.LOADING;
    else if (arr.some((s) => s === libCon.ERROR)) newStatus = libCon.ERROR;
    else if (arr.every((s) => s === libCon.OK)) newStatus = libCon.OK;

    if (newStatus === libCon.OK) {
      if (!isNullOrUndefined(allParticipants) && !isNullOrUndefined(allActionItems)) {
        let newActionItems = dm_fun.filterOnlyGroundTeam({ allActionItems: structuredClone(allActionItems) });
        newActionItems = dm_fun.attachParticipantsToActionItems({
          actionItems: newActionItems,
          participants: structuredClone(allParticipants),
        });
        setActionItems(newActionItems);
        setStatus(libCon.OK);
      } else setStatus(libCon.LOADING);
    } else setStatus(newStatus);
  }, [allParticipants, statusParticipants, allActionItems, statusActionItems]);

  return [status, actionItems];
};

export const RefActiveParticipantsWithSensorData = () => {
  // Participants
  const allParticipants = RefStoredValue(libCon.DATASET_PARTICIPANTS);
  const statusParticipants = RefStoredValue(libCon.STATUS_DATASET_PARTICIPANTS);

  // Sensors
  const sensors = RefStoredValue(libCon.DATASET_SENSORS);
  const statusSensors = RefStoredValue(libCon.STATUS_DATASET_SENSORS);

  // Sensor Placement
  const sensorPlacements = RefStoredValue(libCon.DATASET_SENSOR_HOUSE_PLACEMENT);
  const statusSensorPlacement = RefStoredValue(libCon.STATUS_DATASET_SENSOR_HOUSE_PLACEMENT);

  const [participants, setParticipants] = useState(() => null);
  const [status, setStatus] = useState(() => libCon.STATUS_NOT_STARTED);

  useEffect(() => {
    let arr = [statusParticipants, statusSensors, statusSensorPlacement];

    let newStatus = libCon.STATUS_NOT_STARTED;

    if (arr.some((s) => s === libCon.LOADING)) newStatus = libCon.LOADING;
    else if (arr.some((s) => s === libCon.ERROR)) newStatus = libCon.ERROR;
    else if (arr.every((s) => s === libCon.OK)) newStatus = libCon.OK;

    if (newStatus === libCon.OK) {
      if (allParticipants !== null && sensors !== null && sensorPlacements !== null) {
        let newParticipants = dm_fun.filterOnlySEWA({ allParticipants: structuredClone(allParticipants) });
        newParticipants = dm_fun.filterOnlyActive({ participants: newParticipants });
        newParticipants = dm_fun.attachSensorPlacementToParticipants({
          participants: newParticipants,
          sensors: structuredClone(sensors),
          sensorPlacements: structuredClone(sensorPlacements),
        });
        newParticipants = dm_fun.computeDaysUntilExpiry({ participants: newParticipants });
        newParticipants = dm_fun.computeDaysSinceLastUpload({ participants: newParticipants });
        setParticipants(newParticipants);
        setStatus(libCon.OK);
      } else setStatus(libCon.LOADING);
    } else setStatus(newStatus);
  }, [statusParticipants, statusSensors, statusSensorPlacement, allParticipants, sensors, sensorPlacements]);

  return [status, participants];
};

export const RefActiveParticipantsWithAllPlacements = () => {
  // Participants
  const allParticipants = RefStoredValue(libCon.DATASET_PARTICIPANTS);
  const statusParticipants = RefStoredValue(libCon.STATUS_DATASET_PARTICIPANTS);

  // Houses
  const houses = RefStoredValue(libCon.DATASET_HOUSES);
  const statusHouses = RefStoredValue(libCon.STATUS_DATASET_HOUSES);

  // Emails
  const emails = RefStoredValue(libCon.DATASET_EMAILS);
  const statusEmails = RefStoredValue(libCon.STATUS_DATASET_EMAILS);

  // Sensors
  const sensors = RefStoredValue(libCon.DATASET_SENSORS);
  const statusSensors = RefStoredValue(libCon.STATUS_DATASET_SENSORS);

  // Sensor Placement
  const sensorPlacements = RefStoredValue(libCon.DATASET_SENSOR_HOUSE_PLACEMENT);
  const statusSensorPlacement = RefStoredValue(libCon.STATUS_DATASET_SENSOR_HOUSE_PLACEMENT);

  // Wearables
  const wearables = RefStoredValue(libCon.DATASET_WEARABLES);
  const statusWearables = RefStoredValue(libCon.STATUS_DATASET_WEARABLES);

  // Wearable Placement
  const wearablesPlacements = RefStoredValue(libCon.DATASET_WEARABLE_PLACEMENT);
  const statusWearablesPlacement = RefStoredValue(libCon.STATUS_DATASET_WEARABLE_PLACEMENT);

  // Phones
  const phones = RefStoredValue(libCon.DATASET_PHONES);
  const statusPhones = RefStoredValue(libCon.STATUS_DATASET_PHONES);

  // Phone Placement
  const phonePlacements = RefStoredValue(libCon.DATASET_PHONE_PLACEMENT);
  const statusPhonePlacement = RefStoredValue(libCon.STATUS_DATASET_PHONE_PLACEMENT);

  // Received Sensor Files
  const receivedSensorFiles = RefStoredValue(libCon.DATASET_RECEIVED_SENSOR_FILES);
  const statusReceivedSensorFiles = RefStoredValue(libCon.STATUS_DATASET_RECEIVED_SENSOR_FILES);

  // Action Items
  const actionItems = RefStoredValue(libCon.DATASET_ISSUES);
  const statusActionItems = RefStoredValue(libCon.STATUS_DATASET_ISSUES);

  // Collection Recrods
  const collectionRecords = RefStoredValue(libCon.DATASET_COLLECTION_RECORDS);
  const statusCollectionRecords = RefStoredValue(libCon.STATUS_DATASET_COLLECTION_RECORDS);

  // Participant Placements
  const participantPlacements = RefStoredValue(libCon.DATASET_PARTICIPANT_PLACEMENT);
  const statusParticipantPlacements = RefStoredValue(libCon.STATUS_DATASET_PARTICIPANT_PLACEMENT);

  // Battery Records
  const batteryRecords = RefStoredValue(libCon.DATASET_BATTERY_CHANGE_RECORDS);
  const statusBatteryRecords = RefStoredValue(libCon.STATUS_DATASET_BATTERY_CHANGE_RECORDS);

  // Several of the statuses below are composed into sentences at build time,
  // so the locale has to rebuild them.
  const locale = RefLocale();

  // FInal Participants
  const [participants, setParticipants] = useState(() => null);
  const [status, setStatus] = useState(() => libCon.STATUS_NOT_STARTED);

  useEffect(() => {
    let arr = [
      statusParticipants,
      statusHouses,
      statusEmails,
      statusSensors,
      statusSensorPlacement,
      statusWearables,
      statusWearablesPlacement,
      statusPhones,
      statusPhonePlacement,
      statusReceivedSensorFiles,
      statusActionItems,
      statusCollectionRecords,
      statusParticipantPlacements,
      statusBatteryRecords,
    ];

    let newStatus = libCon.STATUS_NOT_STARTED;

    if (arr.some((s) => s === libCon.LOADING)) newStatus = libCon.LOADING;
    else if (arr.some((s) => s === libCon.ERROR)) newStatus = libCon.ERROR;
    else if (arr.every((s) => s === libCon.OK)) newStatus = libCon.OK;

    let dataArr = [
      allParticipants,
      houses,
      emails,
      sensors,
      sensorPlacements,
      wearables,
      wearablesPlacements,
      phones,
      phonePlacements,
      receivedSensorFiles,
      actionItems,
      collectionRecords,
      participantPlacements,
      batteryRecords,
    ];

    if (newStatus === libCon.OK) {
      if (dataArr.every((s) => !isNullOrUndefined(s))) {
        let newParticipants = dm_fun.filterOnlySEWA({ allParticipants: structuredClone(allParticipants) });

        newParticipants = dm_fun.filterOnlyActive({ participants: newParticipants });
        newParticipants = dm_fun.attachParticipantPlacementsToParticipant({
          participants: newParticipants,
          participantPlacements: participantPlacements,
        });
        newParticipants = dm_fun.attachHousesToParticipants({ participants: newParticipants, houses: houses });
        newParticipants = dm_fun.attachActionItemsToParticipant({ participants: newParticipants, actionItems: actionItems });
        newParticipants = dm_fun.attachCollectionRecordsToParticipant({
          participants: newParticipants,
          collectionRecords: collectionRecords,
        });
        newParticipants = dm_fun.attachSensorPlacementToParticipants({
          participants: newParticipants,
          sensors: structuredClone(sensors),
          sensorPlacements: structuredClone(sensorPlacements),
          batteryRecords: structuredClone(batteryRecords),
        });
        newParticipants = dm_fun.attachReceivedSensorFiles({
          participants: newParticipants,
          receivedSensorFiles: structuredClone(receivedSensorFiles),
        });
        newParticipants = dm_fun.attachPhonePlacement({
          participants: newParticipants,
          phones: phones,
          phonePlacement: phonePlacements,
        });
        newParticipants = dm_fun.attachWearablePlacement({
          participants: newParticipants,
          wearables: wearables,
          wearablePlacement: wearablesPlacements,
          emails: emails,
        });
        newParticipants = dm_fun.computeDaysUntilExpiry({ participants: newParticipants });
        newParticipants = dm_fun.computeDaysSinceLastUpload({ participants: newParticipants });
        newParticipants = dm_fun.computeDeviceCollectionStatus({ participants: newParticipants });

        setParticipants(newParticipants);
        setStatus(libCon.OK);
      } else setStatus(libCon.LOADING);
    } else setStatus(newStatus);
  }, [
    statusParticipants,
    statusSensors,
    statusHouses,
    statusEmails,
    statusSensorPlacement,
    statusWearables,
    statusWearablesPlacement,
    statusPhones,
    statusPhonePlacement,
    statusReceivedSensorFiles,
    statusActionItems,
    allParticipants,
    houses,
    emails,
    sensors,
    sensorPlacements,
    wearables,
    wearablesPlacements,
    phones,
    phonePlacements,
    receivedSensorFiles,
    actionItems,
    statusCollectionRecords,
    collectionRecords,
    statusParticipantPlacements,
    participantPlacements,
    statusBatteryRecords,
    batteryRecords,
    locale,
  ]);

  return [status, participants];
};

export const RefAllParticipantsWithPlacement = () => {
  // Participants
  const allParticipants = RefStoredValue(libCon.DATASET_PARTICIPANTS);
  const statusParticipants = RefStoredValue(libCon.STATUS_DATASET_PARTICIPANTS);

  // Paticipant Placements
  const allParticipantPlacements = RefStoredValue(libCon.DATASET_PARTICIPANT_PLACEMENT);
  const statusParticipantPlacements = RefStoredValue(libCon.STATUS_DATASET_PARTICIPANT_PLACEMENT);

  const [participants, setParticipants] = useState(() => null);
  const [status, setStatus] = useState(() => libCon.STATUS_NOT_STARTED);

  useEffect(() => {
    let arr = [statusParticipantPlacements, statusParticipants];

    let newStatus = libCon.STATUS_NOT_STARTED;

    if (arr.some((s) => s === libCon.LOADING)) newStatus = libCon.LOADING;
    else if (arr.some((s) => s === libCon.ERROR)) newStatus = libCon.ERROR;
    else if (arr.every((s) => s === libCon.OK)) newStatus = libCon.OK;

    if (newStatus === libCon.OK) {
      if (allParticipants !== null && allParticipantPlacements !== null) {
        let newParticipants = dm_fun.filterOnlySEWA({ allParticipants: structuredClone(allParticipants) });

        newParticipants = dm_fun.attachParticipantPlacementsToParticipant({
          participants: newParticipants,
          participantPlacements: allParticipantPlacements,
        });

        setParticipants(newParticipants);
        setStatus(libCon.OK);
      } else setStatus(libCon.LOADING);
    } else setStatus(newStatus);
  }, [statusParticipants, statusParticipantPlacements, allParticipants, allParticipantPlacements]);

  return [status, participants];
};

export const RefAllSensorsWithPlacement = () => {
  // Sensors
  const allSensors = RefStoredValue(libCon.DATASET_SENSORS);
  const statusSensors = RefStoredValue(libCon.STATUS_DATASET_SENSORS);

  // Sensor Placements
  const allSensorPlacements = RefStoredValue(libCon.DATASET_SENSOR_HOUSE_PLACEMENT);
  const statusSensorPlacements = RefStoredValue(libCon.STATUS_DATASET_SENSOR_HOUSE_PLACEMENT);

  // Participants
  const allParticipants = RefStoredValue(libCon.DATASET_PARTICIPANTS);
  const statusParticipants = RefStoredValue(libCon.STATUS_DATASET_PARTICIPANTS);

  const [sensors, setSensors] = useState(() => null);
  const [status, setStatus] = useState(() => libCon.STATUS_NOT_STARTED);

  useEffect(() => {
    let arr = [statusSensors, statusSensorPlacements, statusParticipants];

    let newStatus = libCon.STATUS_NOT_STARTED;

    if (arr.some((s) => s === libCon.LOADING)) newStatus = libCon.LOADING;
    else if (arr.some((s) => s === libCon.ERROR)) newStatus = libCon.ERROR;
    else if (arr.every((s) => s === libCon.OK)) newStatus = libCon.OK;

    if (newStatus === libCon.OK) {
      if (
        !isNullOrUndefined(allSensors) &&
        !isNullOrUndefined(allSensorPlacements) &&
        !isNullOrUndefined(allParticipants)
      ) {
        let newSensors = dm_fun.attachSensorPlacementToSensors({
          sensors: structuredClone(allSensors),
          sensorPlacements: structuredClone(allSensorPlacements),
        });

        newSensors = dm_fun.filterNotDevelopment({ allDevices: newSensors, allParticipants: allParticipants });

        setSensors(newSensors);
        setStatus(libCon.OK);
      } else setStatus(libCon.LOADING);
    } else setStatus(newStatus);
  }, [statusSensors, statusSensorPlacements, statusParticipants, allSensors, allSensorPlacements, allParticipants]);

  return [status, sensors];
};

export const RefAllReceivedSensorFiles = () => {
  // Received Sensor Files
  const receivedSensorFiles = RefStoredValue(libCon.DATASET_RECEIVED_SENSOR_FILES);
  const statusReceivedSensorFiles = RefStoredValue(libCon.STATUS_DATASET_RECEIVED_SENSOR_FILES);

  const [files, setFiles] = useState(() => null);
  const [status, setStatus] = useState(() => libCon.STATUS_NOT_STARTED);

  useEffect(() => {}, [receivedSensorFiles]);

  useEffect(() => {
    let arr = [statusReceivedSensorFiles];

    let newStatus = libCon.STATUS_NOT_STARTED;

    if (arr.some((s) => s === libCon.LOADING)) newStatus = libCon.LOADING;
    else if (arr.some((s) => s === libCon.ERROR)) newStatus = libCon.ERROR;
    else if (arr.every((s) => s === libCon.OK)) newStatus = libCon.OK;

    if (newStatus === libCon.OK) {
      if (!isNullOrUndefined(receivedSensorFiles)) {
        let newFiles = receivedSensorFiles;
        setFiles(newFiles);
        setStatus(libCon.OK);
      } else setStatus(libCon.LOADING);
    } else setStatus(newStatus);
  }, [statusReceivedSensorFiles, receivedSensorFiles]);

  return [status, files];
};

export const RefAllEmails = () => {
  // Received Sensor Files
  const emailRecords = RefStoredValue(libCon.DATASET_EMAILS);
  const statusEmailRecords = RefStoredValue(libCon.STATUS_DATASET_EMAILS);

  const [emails, setEmails] = useState(() => null);
  const [status, setStatus] = useState(() => libCon.STATUS_NOT_STARTED);

  useEffect(() => {
    let arr = [statusEmailRecords];

    let newStatus = libCon.STATUS_NOT_STARTED;

    if (arr.some((s) => s === libCon.LOADING)) newStatus = libCon.LOADING;
    else if (arr.some((s) => s === libCon.ERROR)) newStatus = libCon.ERROR;
    else if (arr.every((s) => s === libCon.OK)) newStatus = libCon.OK;

    if (newStatus === libCon.OK) {
      if (!isNullOrUndefined(emailRecords)) {
        let newEmails = emailRecords;
        setEmails(newEmails);
        setStatus(libCon.OK);
      } else setStatus(libCon.LOADING);
    } else setStatus(newStatus);
  }, [statusEmailRecords, emailRecords]);

  return [status, emails];
};

export const RefCoverageSummary = () => {
  // Received Sensor Files
  const coverageRecords = RefStoredValue(libCon.DATASET_COVERAGE_SUMMARY);
  const statusCoverageRecords = RefStoredValue(libCon.STATUS_DATASET_COVERAGE_SUMMARY);

  const [coverage, setCoverage] = useState(() => null);
  const [status, setStatus] = useState(() => libCon.STATUS_NOT_STARTED);

  useEffect(() => {
    let arr = [statusCoverageRecords];

    let newStatus = libCon.STATUS_NOT_STARTED;

    if (arr.some((s) => s === libCon.LOADING)) newStatus = libCon.LOADING;
    else if (arr.some((s) => s === libCon.ERROR)) newStatus = libCon.ERROR;
    else if (arr.every((s) => s === libCon.OK)) newStatus = libCon.OK;

    if (newStatus === libCon.OK) {
      if (!isNullOrUndefined(coverageRecords)) {
        let newCoverage = coverageRecords;
        setCoverage(newCoverage);
        setStatus(libCon.OK);
      } else setStatus(libCon.LOADING);
    } else setStatus(newStatus);
  }, [statusCoverageRecords, coverageRecords]);

  return [status, coverage];
};
